import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { contentLoaders } from 'redux/content';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Читає розділ CMS і завантажує його при першій появі.
 *
 * Замінює однаковий шматок, що повторювався на кожній сторінці:
 *
 *   useEffect(() => {
 *     if (articles.length === 0) dispatch(getArticles());
 *   }, []);
 *
 * У тій формі був дефект: умовою була порожнеча списку, а не стан запиту.
 * Розділ, який справді порожній, перезапитувався на кожному заході на
 * сторінку, а розділ, чий запит упав, не перезапитувався ніколи, бо помилку
 * ніхто не зберігав.
 *
 * Друга умова завантаження — мова. У CMS переклад лежить окремим записом, тож
 * після перемикання мови наявні дані вже не ті, і розділ береться наново.
 *
 * Повертає { data, status, error, retry, isLoading }.
 */
export function useContent(key) {
  const dispatch = useDispatch();
  const section = useSelector((state) => state[key]);
  const language = useLanguage();

  const load = contentLoaders[key];

  useEffect(() => {
    if (section.status === 'idle' || section.language !== language) dispatch(load(language));
  }, [dispatch, load, language, section.status, section.language]);

  const retry = useCallback(() => dispatch(load(language)), [dispatch, load, language]);

  return {
    ...section,
    retry,
    // Перше завантаження. Повторне оновлення поверх наявних даних екран не
    // гасить: старий список лишається на місці, поки не приїде новий.
    isLoading: section.status === 'loading' || section.status === 'idle',
  };
}
